import { useDispatch, useSelector } from "react-redux";

import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import {
    useInvalidateOpportunityListContactsResults,
    useOpportunityListContactsResults,
} from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useOpportunityListContactsResults";
import ActiveContactsRevealPanel from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/ActiveContactsRevealPanel";
import { useContactsFlowStrategy } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import useActivePanelLifeCycle from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useActivePanelLifeCycle";
import { setIsActionPanelActivated } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/action-creators";
import { revealMultipleContactsByIdWithToastThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects-reveal";
import { selectMultipleRevealIsInProgress } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ContactRevealByIdRequestDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { revealTopNContactsWithToastThunk } from "app/pages/sales-intelligence/sub-modules/opportunities/store/effects";

const ContactsRevealPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const contactsFlowStrategy = useContactsFlowStrategy();

    const listParams = useListParams();

    const { data, isFetching } = useOpportunityListContactsResults();
    const invalidateContacts = useInvalidateOpportunityListContactsResults();
    const isMultipleRevealInProgress = useSelector(selectMultipleRevealIsInProgress);

    useActivePanelLifeCycle();

    async function handleSubmit(topNOrIds: number | readonly ContactRevealByIdRequestDto[], onDone: () => void) {
        try {
            if (typeof topNOrIds === "number") {
                await dispatch(revealTopNContactsWithToastThunk(listParams, topNOrIds));
            } else {
                await dispatch(
                    revealMultipleContactsByIdWithToastThunk(topNOrIds, contactsFlowStrategy.dataSource.revealMultiple),
                );
            }

            invalidateContacts();
            onDone();
        } finally {
            dispatch(setIsActionPanelActivated(false));
        }
    }

    return (
        <ActiveContactsRevealPanel
            isLoading={isMultipleRevealInProgress}
            notRevealedCount={data?.totalCount || 0}
            contactsCount={data?.rows?.length || 0}
            areSearchResultsFetching={isFetching}
            onSubmit={handleSubmit}
            {...props}
        />
    );
};

export default ContactsRevealPanelContainer;
